const parseNumber = (value, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const parseArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value !== 'string' || value.trim() === '') return []
  return value.split(',').map((entry) => entry.trim()).filter(Boolean)
}

const parseBooleanArray = (value) => {
  return parseArray(value).map((entry) => entry === 'true')
}

const serializeArray = (value) => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.join(',')
}

export const cleanupQuery = (queryObject) => {
  return Object.entries(queryObject).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc
    acc[key] = value
    return acc
  }, {})
}

const SESSION_KEY_PREFIX = 'list-route:'

/**
 * When the list URL has no query params, we may restore the last list state
 * (e.g. after sidebar navigation) from sessionStorage.
 */
export const shouldRestoreListStateFromUrlQuery = (query) => {
  return Object.keys(cleanupQuery(query)).length === 0
}

export const readListQueryFromSession = (routeName) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY_PREFIX + routeName)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

export const writeListQueryToSession = (routeName, queryObject) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return
  try {
    const key = SESSION_KEY_PREFIX + routeName
    const cleaned = cleanupQuery(queryObject)
    if (Object.keys(cleaned).length === 0) {
      window.sessionStorage.removeItem(key)
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(cleaned))
    }
  } catch {
    // ignore quota / private mode
  }
}

export const parseTableOptionsFromQuery = (query, defaults) => {
  return {
    ...defaults,
    page: parseNumber(query.page, defaults.page),
    itemsPerPage: parseNumber(query.size, defaults.itemsPerPage),
    sortBy: parseArray(query.sortBy),
    sortDesc: parseBooleanArray(query.sortDesc),
  }
}

export const serializeTableOptionsToQuery = (options) => {
  return cleanupQuery({
    page: options.page,
    size: options.itemsPerPage,
    sortBy: serializeArray(options.sortBy),
    sortDesc: serializeArray(options.sortDesc),
  })
}

export const parseFilterArrayFromQuery = (query, key) => parseArray(query[key])

export const serializeFilterArrayToQuery = (key, value) => {
  return {
    [key]: serializeArray(value),
  }
}

export const parseSearchFromQuery = (query) => (typeof query.q === 'string' ? query.q : '')

export const parseBooleanFromQuery = (query, key, fallback = false) => {
  const v = query[key]
  if (v === true || v === 'true' || v === '1') return true
  if (v === false || v === 'false' || v === '0') return false
  return fallback
}

/** Omit from query when false so URLs stay short; missing key restores `fallback` in parseBooleanFromQuery. */
export const serializeBooleanToQuery = (key, value) => {
  return value === true ? { [key]: 'true' } : {}
}

export const serializeSearchToQuery = (searchQuery) => {
  return {
    q: searchQuery || undefined,
  }
}

export const areQueriesEqual = (left, right) => {
  return JSON.stringify(cleanupQuery(left)) === JSON.stringify(cleanupQuery(right))
}

export const areFilterArraysEqual = (left, right) => {
  const normalize = (value) => (Array.isArray(value) ? [...value].filter(Boolean).sort() : [])
  const a = normalize(left)
  const b = normalize(right)
  return a.length === b.length && a.every((entry, index) => entry === b[index])
}

/**
 * True when search or filter values changed (not pagination/sort alone).
 * Pass old* as undefined on first watch run to avoid resetting after route restore.
 */
export const didSearchOrFilterValuesChange = ({
  search,
  oldSearch,
  filterArrays = [],
  oldFilterArrays = [],
  booleanFilters = [],
  oldBooleanFilters = [],
}) => {
  const isInitialWatch = oldSearch === undefined && oldFilterArrays.length === 0 && oldBooleanFilters.length === 0
  if (isInitialWatch) return false
  if (search !== oldSearch) return true
  if (booleanFilters.some((value, index) => value !== oldBooleanFilters[index])) return true
  return filterArrays.some((current, index) => !areFilterArraysEqual(current, oldFilterArrays[index]))
}

/** When filters/search changed, reset table page to 1. Returns true if page was changed. */
export const resetTablePageOnFilterChange = (optionsRef, changed) => {
  if (!changed || optionsRef.value.page === 1) return false
  optionsRef.value = { ...optionsRef.value, page: 1 }
  return true
}

/** After fetch: stale page in URL/session (e.g. page 2 + new search) — reset and refetch once. */
export const shouldResetTablePageAfterFetch = ({ success, page, total, pageSize }) => {
  if (page <= 1) return false
  if (!success) return true
  const maxPage = Math.max(1, Math.ceil(total / pageSize))
  return page > maxPage
}
